import { createContext, useContext, useEffect, useState } from 'react';
import api from '../api';

const TenantContext = createContext({ tenant: null, loading: true });

export const useTenant = () => useContext(TenantContext);

/**
 * TenantProvider
 *
 * Boots the tenant context BEFORE rendering login/register/reset-password.
 * Queries `/api/public/tenant-context` (relative URL so it goes through the
 * same host the user is on — critical for Cloudflare Worker to inject headers).
 *
 * Applies:
 *  - CSS variables (:root) for tenant colors when branding is active
 *  - document.title
 *  - <link rel="icon"> favicon
 *
 * Never trusts client-side hostname alone; the backend is the source of truth.
 */
export function TenantProvider({ children }) {
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refetch = async () => {
    setLoading(true);
    try {
      // IMPORTANT: use relative URL so the request goes through the same
      // origin the user is on (ucm.simuclinic.cl), letting the Worker inject
      // its tenant headers.
      const resp = await fetch('/api/public/tenant-context', {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
        credentials: 'include',
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setTenant(data);
      applyTenantSideEffects(data);
    } catch (e) {
      // If the tenant-context fails, still let the app boot in global mode
      setError(e.message || 'tenant context failed');
      setTenant({ type: 'global', tenant: null, powered_by_simuclinic: true });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refetch(); /* eslint-disable-next-line */ }, []);

  return (
    <TenantContext.Provider value={{ tenant, loading, error, refetch }}>
      {children}
    </TenantContext.Provider>
  );
}

/**
 * Apply CSS variables, favicon and title based on tenant response.
 * Safe to call multiple times; falls back to SimuClinic defaults.
 */
function applyTenantSideEffects(data) {
  const root = document.documentElement;
  const branding = data?.branding;
  const est = data?.establishment;
  const inst = data?.institution;

  // Title
  const parts = [];
  if (est?.short_name) parts.push(est.short_name);
  else if (est?.name) parts.push(est.name);
  else if (inst?.name) parts.push(inst.name);
  parts.push('SimuClinic');
  document.title = parts.join(' | ');

  // CSS variables
  if (branding) {
    if (branding.primary_color)   root.style.setProperty('--tenant-primary', branding.primary_color);
    if (branding.secondary_color) root.style.setProperty('--tenant-secondary', branding.secondary_color);
    if (branding.accent_color)    root.style.setProperty('--tenant-accent', branding.accent_color);
    if (branding.login_background_url) root.style.setProperty('--tenant-login-bg', `url(${branding.login_background_url})`);
  } else {
    // Reset to defaults
    root.style.removeProperty('--tenant-primary');
    root.style.removeProperty('--tenant-secondary');
    root.style.removeProperty('--tenant-accent');
    root.style.removeProperty('--tenant-login-bg');
  }

  // Favicon
  const faviconUrl = branding?.favicon_url;
  const link = document.querySelector("link[rel~='icon']") || (() => {
    const l = document.createElement('link');
    l.rel = 'icon';
    document.head.appendChild(l);
    return l;
  })();
  if (faviconUrl) {
    link.href = faviconUrl;
  } else {
    link.href = '/favicon.ico';
  }
}
